import React, { SetStateAction, useEffect } from 'react';
import { Loader } from '../components/loader/Loader';
import { PARAMS, TOKEN } from '../constants';
import { useLocation } from 'react-router-dom';

import * as IntegrationApi from '../api/integrationApi';
import * as WebAppApi from '../api/webAppApi';
import * as JwtApi from '../api/authorizationApi';

export interface Integration {
  status: string;
  secretKey: string;
  secretKeySecondary: string;
  spaceId: string;
  wixMerchantId: string;
  scope: string;
  activated: boolean;
  returnUrl?: string;
}

export interface IntegrationContextProps {
  integration: Integration;
  activateIntegration: (onSuccess: Function, onFailure: Function) => void;
  createSecondarySecretKey: (onSuccess: Function, onFailure: Function) => void;
  deleteSecondarySecretKey: (onSuccess: Function, onFailure: Function) => void;
  switchSecondarySecretKey: (onSuccess: Function, onFailure: Function) => void;
  uninstallWebApp: (onSuccess: Function, onFailure: Function) => void;
}

export const IntegrationContext = React.createContext(
  {} as IntegrationContextProps
);

export const IntegrationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // @ts-ignore
  const [integration, setIntegration] = React.useState<Integration>(null);
  const [accessToken, setAccessToken] = React.useState<string | null>(null);

  //Retrieve or reuse token
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get(PARAMS.CODE) || '';

  // Handle Auth
  useEffect(() => {
    if (code) {
      JwtApi.getJwt(
        code,
        (responseData: SetStateAction<any>) => {
          localStorage.setItem(TOKEN, responseData.accessToken);
          setAccessToken(responseData.accessToken);
        },
        () => console.error('Could not retrieve JWT token')
      );
    } else {
      const token = localStorage.getItem(TOKEN);

      if (token) setAccessToken(localStorage.getItem(TOKEN));
      else console.error('No JWT token set in local storage');
    }
  }, [code]);

  // When Token is ready load data
  useEffect(() => {
    if (accessToken) {
      IntegrationApi.getIntegration(
        (data: Integration) => setIntegration(data),
        () => console.error('Failed to load integration')
      );
    }
  }, [accessToken]);

  // Update Favicon
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (link && integration) {
      link.setAttribute('href', `/${integration.scope}-favicon.png`);
    }
  }, [integration]);

  const activateIntegration = (onSuccess: Function, onFailure: Function) => {
    IntegrationApi.activateIntegration((data: Integration) => {
      setIntegration(data);
      onSuccess(data);
    }, onFailure);
  };

  const createSecondarySecretKey = (
    onSuccess: Function,
    onFailure: Function
  ) => {
    IntegrationApi.createSecondarySecretKey((data: Integration) => {
      setIntegration(data);
      onSuccess(data);
    }, onFailure);
  };

  const deleteSecondarySecretKey = (
    onSuccess: Function,
    onFailure: Function
  ) => {
    IntegrationApi.deleteSecondarySecretKey((data: Integration) => {
      setIntegration(data);
      onSuccess(data);
    }, onFailure);
  };

  const switchSecondarySecretKey = (
    onSuccess: Function,
    onFailure: Function
  ) => {
    IntegrationApi.switchSecondarySecretKey((data: Integration) => {
      setIntegration(data);
      onSuccess(data);
    }, onFailure);
  };

  const uninstallWebApp = (onSuccess: Function, onFailure: Function) => {
    WebAppApi.uninstallWebApp((data: any) => {
      onSuccess(data);
    }, onFailure);
  };

  const value = {
    integration,
    activateIntegration,
    createSecondarySecretKey,
    deleteSecondarySecretKey,
    switchSecondarySecretKey,
    uninstallWebApp,
  };

  return (
    <IntegrationContext.Provider value={value}>
      {integration ? children : <Loader />}
    </IntegrationContext.Provider>
  );
};
